import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiClient as ApiClientSchema, ApiClientWithKey, ApiScope, Member } from '@cloudsign/shared';
import type { ApiClient, ApiClientCreate, UnmatchedPerson, UnmatchedResolve } from '@cloudsign/shared';
import { useSearchParams } from 'react-router';
import { Alert, Button, Card, CheckboxField, ConfirmButton, EmptyRow, EmptyState, PageHeader, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { SecretDialog, StatusPill } from '../../components/phase2';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { MemberPage, clientsKey, unmatchedKey, useClients, useGroups, useSites, useUnmatched } from './api';

const SCOPES = ApiScope.options;

/** Scopes that existed before Phase 4 keep their own labels; the Phase 4 ones use the shared apiScope keys. */
const EARLIER_SCOPES: readonly string[] = ['visits.sign_in', 'visits.sign_out', 'visits.on_site', 'webhooks.test'];
const scopeLabel = (s: string): string => (EARLIER_SCOPES.includes(s) ? t(`peo.scope.${s}`) : t(`apiScope.${s}`));
const scopeHelp = (s: string): string | undefined => (EARLIER_SCOPES.includes(s) ? t(`peo.scope.${s}.help`) : undefined);
const SCOPE_GROUPS: { key: 'read' | 'write' | 'signin'; match: (s: string) => boolean }[] = [
  { key: 'read', match: (s) => s.endsWith('.read') || s === 'visits.on_site' },
  { key: 'write', match: (s) => s.endsWith('.write') || s === 'webhooks.test' },
  { key: 'signin', match: (s) => s === 'visits.sign_in' || s === 'visits.sign_out' },
];

/** A date picked in the form becomes the end of that day (UTC). */
const dateToIso = (d: string): string => `${d}T23:59:59.000Z`;

function scopeLabels(scopes: readonly string[]): string {
  return scopes.map(scopeLabel).join(', ');
}

/** API clients: keys for other systems, and the list of people they sent that we could not match. */
export function ApiClientsPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'matching' ? 'matching' : 'clients';
  const go = (next: 'clients' | 'matching') => setParams(next === 'clients' ? {} : { tab: next, ...(params.get('system') ? { system: params.get('system')! } : {}) });
  return (
    <div>
      <PageHeader
        title={t('peo.clients.title')}
        description={t('peo.clients.description')}
        actions={
          <a href="/api/v1/openapi.json" target="_blank" rel="noopener noreferrer" title={t('p4w27.api.docsHelp')} className="inline-flex min-h-10 items-center rounded-md border border-slate-300 bg-white px-3.5 text-sm font-medium hover:bg-slate-50 sm:min-h-9">
            {t('p4w27.api.docs')}
          </a>
        }
      />
      <div role="tablist" aria-label={t('peo.clients.title')} className="mb-4 flex gap-1 border-b border-slate-200">
        {(['clients', 'matching'] as const).map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            id={`peo-tab-${k}`}
            aria-selected={tab === k}
            aria-controls="peo-tabpanel"
            onClick={() => go(k)}
            className={`-mb-px min-h-10 border-b-2 px-4 text-sm font-medium ${tab === k ? 'border-[var(--brand-primary)] text-slate-900' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
          >
            {t(`peo.tab.${k}`)}
          </button>
        ))}
      </div>
      <div role="tabpanel" id="peo-tabpanel" aria-labelledby={`peo-tab-${tab}`}>
        {tab === 'clients' ? <ClientsTab /> : <MatchingTab initialSystem={params.get('system') ?? ''} />}
      </div>
    </div>
  );
}

// ---------- Clients ----------

function ClientsTab() {
  const q = useClients();
  const canManage = useCan('api_clients.manage');
  const canDelete = useCan('api_clients.delete');
  const qc = useQueryClient();
  const toast = useToast();
  const sites = useSites();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const editClient = q.data?.find((c) => c.id === editing);
  const [secret, setSecret] = useState<{ key: string; created: boolean } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: clientsKey });
  const newKey = useMutation({
    mutationFn: (id: string) => api.post(`/api-clients/${id}/new-key`, { schema: ApiClientWithKey }),
    onSuccess: (r) => {
      setSecret({ key: r.apiKey, created: false });
      void refresh();
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/api-clients/${id}`),
    onSuccess: () => {
      toast(t('peo.clients.revoked'));
      void refresh();
    },
  });
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return <Spinner />;
  const siteName = (id: string) => sites.data?.find((s) => s.id === id)?.name ?? id;
  const sitesText = (c: ApiClient) => (c.siteIds === null ? t('peo.clients.allSites') : c.siteIds.length === 0 ? t('peo.clients.noSites') : c.siteIds.map(siteName).join(', '));
  const add = canManage ? (
    <Button variant="primary" onClick={() => setCreating(true)}>
      {t('peo.clients.add')}
    </Button>
  ) : undefined;
  return (
    <div className="space-y-4">
      {creating ? (
        <CreateClient
          onCancel={() => setCreating(false)}
          onCreated={(r) => {
            setCreating(false);
            setSecret({ key: r.apiKey, created: true });
            void refresh();
          }}
        />
      ) : (
        <div className="flex justify-end">{add}</div>
      )}
      {editClient ? (
        <EditLimits
          key={editClient.id}
          client={editClient}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast(t('p4w27.api.saved'));
            void refresh();
          }}
        />
      ) : null}
      <ErrorLine error={newKey.error ?? revoke.error} />
      <TableWrap caption={t('peo.clients.title')}>
        <thead>
          <tr>
            <Th>{t('peo.clients.name')}</Th>
            <Th>{t('peo.clients.scopes')}</Th>
            <Th>{t('peo.clients.sites')}</Th>
            <Th>{t('p4w27.api.expires')}</Th>
            <Th>{t('p4w27.api.rate')}</Th>
            <Th>{t('peo.clients.lastUsed')}</Th>
            <Th>{t('common.status')}</Th>
            <Th>
              <span className="sr-only">{t('common.actions')}</span>
            </Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {q.data.length === 0 ? <EmptyRow cols={8} action={add} /> : null}
          {q.data.map((c) => {
            const managed = c.ownerModule !== null;
            const active = c.status === 'active';
            return (
              <tr key={c.id}>
                <td className="px-3 py-2 align-top">
                  <span className="font-medium">{c.name}</span>
                  <span className="block font-mono text-xs text-slate-600">{c.keyId}</span>
                  {c.system ? <span className="block text-xs text-slate-600">{t('peo.clients.system', { system: c.system })}</span> : null}
                  {managed ? <span className="block text-xs text-slate-700">{t('peo.clients.managed')}</span> : null}
                </td>
                <td className="px-3 py-2 align-top text-sm">{scopeLabels(c.scopes)}</td>
                <td className="px-3 py-2 align-top text-sm">{sitesText(c)}</td>
                <td className="px-3 py-2 align-top text-sm">
                  {c.expiresAt ? formatDateTime(c.expiresAt) : t('p4w27.api.neverExpires')}
                  {c.expiresAt && new Date(c.expiresAt).getTime() < Date.now() ? (
                    <span className="mt-1 block">
                      <StatusPill tone="bad">{t('p4w27.api.expired')}</StatusPill>
                    </span>
                  ) : null}
                </td>
                <td className="px-3 py-2 align-top text-sm">{c.ratePerMinute ? t('p4w27.api.rateValue', { count: c.ratePerMinute }) : t('p4w27.api.rateDefault')}</td>
                <td className="px-3 py-2 align-top text-sm">{c.lastUsedAt ? formatDateTime(c.lastUsedAt) : t('peo.clients.never')}</td>
                <td className="px-3 py-2 align-top">
                  <StatusPill tone={active ? 'good' : 'neutral'}>{active ? t('peo.clients.active') : t('peo.clients.revokedState')}</StatusPill>
                </td>
                <td className="px-3 py-2 align-top">
                  {!managed && active ? (
                    <div className="flex flex-wrap justify-end gap-2">
                      {canManage ? (
                        <Button aria-label={t('p4w27.api.editLabel', { name: c.name })} aria-expanded={editing === c.id} onClick={() => setEditing(editing === c.id ? null : c.id)}>
                          {t('p4w27.api.edit')}
                        </Button>
                      ) : null}
                      {canManage ? <ConfirmButton label={t('peo.clients.newKey')} confirmLabel={t('peo.clients.newKeyConfirm')} pending={newKey.isPending} onConfirm={() => newKey.mutate(c.id)} /> : null}
                      {canDelete ? <ConfirmButton label={t('peo.clients.revoke')} confirmLabel={t('peo.clients.revokeConfirm')} pending={revoke.isPending} onConfirm={() => revoke.mutate(c.id)} /> : null}
                    </div>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </TableWrap>
      {secret ? (
        <SecretDialog
          title={secret.created ? t('peo.clients.secret.created') : t('peo.clients.secret.rotated')}
          description={t('peo.clients.secret.description')}
          secret={secret.key}
          secretLabel={t('peo.clients.secret.label')}
          onClose={() => setSecret(null)}
        />
      ) : null}
    </div>
  );
}

/** Expiry date and request limit fields, shared by the create and edit forms. */
function LimitFields({ expiry, rate, onExpiry, onRate, errs }: { expiry: string; rate: string; onExpiry: (v: string) => void; onRate: (v: string) => void; errs: Record<string, string> }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <TextField type="date" label={t('p4w27.api.expiryLabel')} hint={t('p4w27.api.expiryHint')} value={expiry} onChange={(e) => onExpiry(e.target.value)} error={errs['expiresAt']} />
      <TextField
        type="number"
        inputMode="numeric"
        min={1}
        max={100000}
        label={t('p4w27.api.rateLabel')}
        hint={t('p4w27.api.rateHint')}
        value={rate}
        onChange={(e) => onRate(e.target.value)}
        error={errs['ratePerMinute']}
      />
    </div>
  );
}

const limitBody = (expiry: string, rate: string) => ({
  expiresAt: expiry ? dateToIso(expiry) : null,
  ratePerMinute: rate.trim() === '' ? null : Number(rate),
});
const limitsValid = (rate: string) => rate.trim() === '' || (Number.isInteger(Number(rate)) && Number(rate) >= 1 && Number(rate) <= 100000);

function EditLimits({ client, onCancel, onSaved }: { client: ApiClient; onCancel: () => void; onSaved: () => void }) {
  const [expiry, setExpiry] = useState(client.expiresAt ? client.expiresAt.slice(0, 10) : '');
  const [rate, setRate] = useState(client.ratePerMinute ? String(client.ratePerMinute) : '');
  const save = useMutation({
    mutationFn: () => api.patch(`/api-clients/${client.id}`, { body: limitBody(expiry, rate), schema: ApiClientSchema }),
    onSuccess: onSaved,
  });
  return (
    <Card
      title={t('p4w27.api.editLabel', { name: client.name })}
      footer={
        <>
          <Button onClick={onCancel}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={!limitsValid(rate) || save.isPending} onClick={() => save.mutate()}>
            {t('p4w27.api.editSave')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <LimitFields expiry={expiry} rate={rate} onExpiry={setExpiry} onRate={setRate} errs={fieldErrors(save.error)} />
        <ErrorLine error={save.error} />
      </div>
    </Card>
  );
}

function CreateClient({ onCancel, onCreated }: { onCancel: () => void; onCreated: (r: ApiClientWithKey) => void }) {
  const sites = useSites();
  const groups = useGroups();
  const [name, setName] = useState('');
  const [system, setSystem] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  const [allSites, setAllSites] = useState(true);
  const [siteIds, setSiteIds] = useState<string[]>([]);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [defaultGroupId, setDefaultGroupId] = useState('');
  const [expiry, setExpiry] = useState('');
  const [rate, setRate] = useState('');
  const create = useMutation({
    mutationFn: (body: ApiClientCreate) => api.post('/api-clients', { body, schema: ApiClientWithKey }),
    onSuccess: onCreated,
  });
  const toggle = (list: string[], id: string, on: boolean) => (on ? [...list, id] : list.filter((x) => x !== id));
  const errs = fieldErrors(create.error);
  const valid = name.trim() !== '' && scopes.length > 0 && (allSites || siteIds.length > 0) && limitsValid(rate);
  const submit = () =>
    create.mutate({
      name: name.trim(),
      system: system.trim() === '' ? null : system.trim(),
      scopes: scopes as ApiClientCreate['scopes'],
      siteIds: allSites ? null : siteIds,
      groupIds,
      defaultGroupId: defaultGroupId && groupIds.includes(defaultGroupId) ? defaultGroupId : null,
      ...(expiry ? { expiresAt: dateToIso(expiry) } : {}),
      ...(rate.trim() !== '' ? { ratePerMinute: Number(rate) } : {}),
    });
  return (
    <Card
      title={t('peo.clients.create.title')}
      footer={
        <>
          <Button onClick={onCancel}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={!valid || create.isPending} onClick={submit}>
            {t('peo.clients.create.submit')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <TextField label={t('peo.clients.create.name')} value={name} maxLength={100} onChange={(e) => setName(e.target.value)} error={errs['name']} />
        <TextField label={t('peo.clients.create.system')} hint={t('peo.clients.create.systemHelp')} value={system} maxLength={30} onChange={(e) => setSystem(e.target.value)} error={errs['system']} />
        <fieldset className="space-y-4">
          <legend className="mb-1 text-sm font-medium text-slate-800">{t('peo.clients.create.scopes')}</legend>
          {SCOPE_GROUPS.map((g) => {
            const list = SCOPES.filter(g.match);
            if (list.length === 0) return null;
            return (
              <div key={g.key} role="group" aria-label={t(`p4w27.api.group.${g.key}`)} className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">{t(`p4w27.api.group.${g.key}`)}</p>
                {list.map((s) => (
                  <CheckboxField key={s} label={scopeLabel(s)} hint={scopeHelp(s)} checked={scopes.includes(s)} onChange={(e) => setScopes(toggle(scopes, s, e.target.checked))} />
                ))}
              </div>
            );
          })}
        </fieldset>
        <LimitFields expiry={expiry} rate={rate} onExpiry={setExpiry} onRate={setRate} errs={errs} />
        <fieldset className="space-y-2">
          <legend className="mb-1 text-sm font-medium text-slate-800">{t('peo.clients.create.sites')}</legend>
          <CheckboxField label={t('peo.clients.allSites')} checked={allSites} onChange={(e) => setAllSites(e.target.checked)} />
          {!allSites ? (sites.data ?? []).map((s) => <CheckboxField key={s.id} label={s.name} checked={siteIds.includes(s.id)} onChange={(e) => setSiteIds(toggle(siteIds, s.id, e.target.checked))} />) : null}
        </fieldset>
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-slate-800">{t('peo.clients.create.groups')}</legend>
          <p className="text-xs text-slate-600">{t('peo.clients.create.groupsHelp')}</p>
          {(groups.data ?? []).map((g) => (
            <CheckboxField key={g.id} label={g.name} checked={groupIds.includes(g.id)} onChange={(e) => setGroupIds(toggle(groupIds, g.id, e.target.checked))} />
          ))}
        </fieldset>
        {groupIds.length > 0 ? (
          <SelectField label={t('peo.clients.create.defaultGroup')} hint={t('peo.clients.create.defaultGroupHelp')} value={defaultGroupId} onChange={(e) => setDefaultGroupId(e.target.value)}>
            <option value="">{t('peo.clients.create.noDefault')}</option>
            {(groups.data ?? []).filter((g) => groupIds.includes(g.id)).map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </SelectField>
        ) : null}
        <ErrorLine error={create.error} />
      </div>
    </Card>
  );
}

// ---------- Needs matching ----------

const SYSTEM_PATTERN = /^[a-z][a-z0-9_-]{1,29}$/;

function MatchingTab({ initialSystem }: { initialSystem: string }) {
  const clients = useClients();
  const systems = Array.from(new Set([initialSystem, ...(clients.data ?? []).map((c) => c.system ?? '')].filter((s) => SYSTEM_PATTERN.test(s))));
  const [picked, setPicked] = useState('');
  const system = picked || systems[0] || '';
  const list = useUnmatched(system);
  const [open, setOpen] = useState<string | null>(null);
  if (clients.isError) return <ErrorPanel error={clients.error} onRetry={() => void clients.refetch()} />;
  if (!clients.data) return <Spinner />;
  if (systems.length === 0) return <EmptyState message={t('peo.match.noSystems')} />;
  return (
    <div className="space-y-4">
      <SelectField label={t('peo.match.system')} value={system} onChange={(e) => setPicked(e.target.value)} className="max-w-xs">
        {systems.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </SelectField>
      <p className="text-sm text-slate-600">{t('peo.match.help')}</p>
      {list.isError ? (
        <ErrorPanel error={list.error} onRetry={() => void list.refetch()} />
      ) : !list.data ? (
        <Spinner />
      ) : list.data.length === 0 ? (
        <EmptyState message={t('peo.match.empty')} />
      ) : (
        <ul className="space-y-3">
          {list.data.map((p) => (
            <UnmatchedRow key={p.id} person={p} system={system} open={open === p.id} onToggle={() => setOpen(open === p.id ? null : p.id)} />
          ))}
        </ul>
      )}
    </div>
  );
}

type Mode = 'link' | 'create';

function UnmatchedRow({ person, system, open, onToggle }: { person: UnmatchedPerson; system: string; open: boolean; onToggle: () => void }) {
  const canManage = useCan('api_clients.manage');
  const canCreate = useCan('groups.manage_members');
  const qc = useQueryClient();
  const toast = useToast();
  const [mode, setMode] = useState<Mode>('link');
  const [groupId, setGroupId] = useState('');
  const [search, setSearch] = useState('');
  const groups = useGroups();
  const repeat = (groups.data ?? []).filter((g) => g.kind === 'repeat');
  const gid = groupId || repeat[0]?.id || '';
  const members = useQuery({
    queryKey: ['people', 'members', gid, search],
    enabled: open && mode === 'link' && gid !== '',
    queryFn: () => api.get('/members', { query: { groupId: gid, search: search || undefined, pageSize: 8 }, schema: MemberPage }),
  });
  const resolve = useMutation({
    mutationFn: (body: UnmatchedResolve) => api.post(`/api-clients/unmatched/${person.id}/resolve`, { body }),
    onSuccess: () => {
      toast(t('peo.match.resolved'));
      void qc.invalidateQueries({ queryKey: unmatchedKey(system) });
    },
  });
  const groupSelect = (
    <SelectField label={t('peo.match.group')} value={gid} onChange={(e) => setGroupId(e.target.value)}>
      {repeat.map((g) => (
        <option key={g.id} value={g.id}>
          {g.name}
        </option>
      ))}
    </SelectField>
  );
  return (
    <li className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium">{person.name}</p>
          <p className="text-sm text-slate-600">
            {[person.emailMasked, t('peo.match.externalId', { id: person.externalId }), t('peo.match.seen', { when: formatDateTime(person.lastSeenAt), count: person.attempts })].filter(Boolean).join(' · ')}
          </p>
        </div>
        {canManage ? (
          <div className="flex flex-wrap gap-2">
            <Button aria-expanded={open} onClick={onToggle}>
              {t('peo.match.resolve')}
            </Button>
            <ConfirmButton label={t('peo.match.dismiss')} confirmLabel={t('peo.match.dismissConfirm')} pending={resolve.isPending} onConfirm={() => resolve.mutate({ action: 'dismiss' })} />
          </div>
        ) : null}
      </div>
      {open ? (
        <div className="mt-4 space-y-3 border-t border-slate-200 pt-4">
          <div className="flex gap-2" role="group" aria-label={t('peo.match.resolve')}>
            <Button variant={mode === 'link' ? 'primary' : 'secondary'} aria-pressed={mode === 'link'} onClick={() => setMode('link')}>
              {t('peo.match.link')}
            </Button>
            {canCreate ? (
              <Button variant={mode === 'create' ? 'primary' : 'secondary'} aria-pressed={mode === 'create'} onClick={() => setMode('create')}>
                {t('peo.match.create')}
              </Button>
            ) : null}
          </div>
          {repeat.length === 0 ? <Alert kind="info">{t('peo.match.noGroups')}</Alert> : groupSelect}
          {mode === 'link' ? (
            <>
              <TextField label={t('peo.match.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
              {members.data && members.data.items.length === 0 ? <p className="text-sm text-slate-600">{t('peo.match.noMembers')}</p> : null}
              <ul className="divide-y divide-slate-100">
                {(members.data?.items ?? []).map((m: Member) => (
                  <li key={m.id} className="flex items-center justify-between gap-3 py-2">
                    <span className="text-sm">
                      {m.fullName}
                      {m.email ? <span className="text-slate-600"> ({m.email})</span> : null}
                    </span>
                    <Button disabled={resolve.isPending} aria-label={t('peo.match.linkTo', { name: m.fullName })} onClick={() => resolve.mutate({ action: 'link', memberId: m.id })}>
                      {t('peo.match.linkButton')}
                    </Button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <Button variant="primary" disabled={gid === '' || resolve.isPending} onClick={() => resolve.mutate({ action: 'create', groupId: gid })}>
              {t('peo.match.createButton', { name: person.name })}
            </Button>
          )}
          <ErrorLine error={resolve.error} />
        </div>
      ) : (
        <ErrorLine error={resolve.error} />
      )}
    </li>
  );
}

